import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Box, Typography, Chip, Badge, Tooltip } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

const getStatusColor = (status) => {
  if (status === 100) return { bg: '#4caf50', border: '#2e7d32', text: '#ffffff' };
  if (status >= 50) return { bg: '#ff9800', border: '#f57c00', text: '#000000' };
  return { bg: '#f44336', border: '#c62828', text: '#ffffff' };
};

const getStatusLabel = (status) => {
  if (status === 100) return 'Good';
  if (status >= 50) return 'Warning';
  return 'Critical';
};

function MetricGroupNode({ data, selected }) {
  const colors = getStatusColor(data.status);
  const hasErrors = data.errors && data.errors.length > 0;

  return (
    <Box
      sx={{
        position: 'relative',
        background: colors.bg,
        border: `2px solid ${selected ? '#1976d2' : colors.border}`,
        borderRadius: 2,
        padding: '12px 16px',
        minWidth: 150,
        boxShadow: selected
          ? '0 0 10px rgba(25, 118, 210, 0.5)'
          : '0 2px 5px rgba(0,0,0,0.2)',
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
        },
      }}
    >
      {/* Error Indicator Badge */}
      {hasErrors && (
        <Tooltip
          title={`Errors: ${data.errors.join(', ')}`}
          placement="top"
        >
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              backgroundColor: '#d32f2f',
              borderRadius: '50%',
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              border: '2px solid white',
              zIndex: 1,
            }}
          >
            <ErrorIcon sx={{ color: 'white', fontSize: 14 }} />
          </Box>
        </Tooltip>
      )}
      {/* Input Handle (left) */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: '#555',
          width: 10,
          height: 10,
          border: '2px solid white',
        }}
      />

      {/* Node Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ color: colors.text, textAlign: 'center' }}
        >
          {data.groupName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={getStatusLabel(data.status)}
            size="small"
            sx={{
              backgroundColor: 'rgba(0,0,0,0.2)',
              color: colors.text,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
          <Chip
            label={`L${data.level}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(0,0,0,0.2)',
              color: colors.text,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
        </Box>
      </Box>

      {/* Output Handle (right) */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: '#555',
          width: 10,
          height: 10,
          border: '2px solid white',
        }}
      />
    </Box>
  );
}

export default memo(MetricGroupNode);
